import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  CommentMessage,
  initials,
  Tag,
} from "@hulahoop/ui";
import { Check } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import type { CommentThreadDetail, CommentPerson } from "@/domain/comment";
import type { CommentsLabels } from "@/domain/types/commentsLabels";
import { commentMentionBody, commentMentionText } from "@/utils/commentMentions";
import { CommentsComposer } from "@/components/shared/forms/CommentsComposer";

export function CommentsThreadView({
  thread,
  actor,
  people,
  labels,
  href,
  link,
  global,
  busy,
  relativeTime,
  onReply,
  onEdit,
  onDelete,
  onReact,
}: {
  thread: CommentThreadDetail;
  actor: CommentPerson | null;
  people: CommentPerson[];
  labels: CommentsLabels;
  href: string | null;
  link: string;
  global: boolean;
  busy: boolean;
  relativeTime: (date: string) => string;
  onReply: (body: string) => Promise<boolean>;
  onEdit: (uuid: string, body: string) => void;
  onDelete: (uuid: string) => void;
  onReact: (uuid: string, emoji: string, remove: boolean) => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
      {global && href && (
        <Button
          variant="ghost"
          size="inline"
          className="-mx-1 self-start px-1 py-0.5"
          aria-label={labels.openRecord(thread.subject.label)}
          render={<Link to={href} />}
        >
          <Tag>{thread.subject.label}</Tag>
        </Button>
      )}
      {global && !href && <Tag className="self-start">{thread.subject.label}</Tag>}
      {thread.messages.map((message) => (
        <CommentMessage
          key={message.uuid}
          avatar={
            <Avatar size="xs">
              {message.author?.avatar && <AvatarImage src={message.author.avatar} alt="" />}
              <AvatarFallback>
                {initials(message.author?.name ?? labels.unknown_author)}
              </AvatarFallback>
            </Avatar>
          }
          author={message.author?.name ?? labels.unknown_author}
          time={`${relativeTime(message.created_at)}${message.edited_at ? ` · ${labels.edited}` : ""}`}
          text={
            message.deleted_at ? labels.deleted : commentMentionText(message.body, message.mentions)
          }
          reactions={message.reactions}
          onReact={
            message.deleted_at || busy
              ? undefined
              : (emoji) =>
                  onReact(
                    message.uuid,
                    emoji,
                    message.reactions.some((reaction) => reaction.emoji === emoji && reaction.me),
                  )
          }
          link={link}
          onEdit={
            !message.deleted_at && actor?.uuid === message.author?.uuid && !busy
              ? (body) => onEdit(message.uuid, commentMentionBody(body, message.mentions))
              : undefined
          }
          onDelete={
            !message.deleted_at && actor?.uuid === message.author?.uuid && !busy
              ? () => onDelete(message.uuid)
              : undefined
          }
          labels={{
            react: labels.reaction,
            more: labels.message_actions,
            copyLink: labels.copy_link,
            linkCopied: labels.link_copied,
            edit: labels.edit,
            editField: labels.edit_field,
            editHint: labels.edit_hint,
            delete: labels.delete,
            deleteTitle: labels.message_delete_title,
            deleteDescription: labels.message_delete_description,
            deleted: labels.message_deleted,
          }}
        />
      ))}
      {thread.resolved_at ? (
        <p className="flex items-center gap-1.5 text-(length:--font-caption) text-muted-foreground">
          <Check aria-hidden />
          {labels.thread_resolved}
        </p>
      ) : (
        <CommentsComposer
          avatar={
            <Avatar size="xs">
              <AvatarFallback>{initials(actor?.name ?? "")}</AvatarFallback>
            </Avatar>
          }
          placeholder={labels.reply_placeholder}
          actionLabel={labels.reply}
          people={people}
          labels={labels}
          disabled={busy}
          onSubmit={onReply}
        />
      )}
    </div>
  );
}
